import { Link, useParams } from "@tanstack/react-router";
import { useT } from "../i18n";
import { LoroText, UndoManager, type TreeID } from "loro-crdt";
import { CursorEphemeralStore } from "loro-prosemirror";
import { useEffect, useMemo, useState } from "react";
import { getMeta, setMeta } from "../sync/persistence";
import { useMe, usePage, useWorkspace } from "../api/hooks";
import { BlockTree } from "../blocks/BlockTree";
import { ensurePageInit, pageTitleText } from "../blocks/model";
import { allFocus, bridgeCursors, setFocus } from "../sync/presence";
import { useDoc, useJoinSettled, useLoroSelector, useSyncStatus } from "../sync/hooks";
import { PageEditorContext, type FocusTarget, type PageEditorContextValue } from "./pageEditorContext";
import { SyncBadge } from "./SyncBadge";
import { PinToggle } from "./PinToggle";
import { LinksPanel } from "../links/LinksPanel";
import { blockSubject, pageNodeOf, pageSubject } from "../links/subject";
import { RoleDialog } from "../links/RoleDialog";
import { addMember, readSequences } from "../sequences/model";
import { blockIri } from "../lib/iri";
import { ExtractButton } from "../review/ExtractButton";
import { RelationsMenu } from "../rail/RelationsMenu";
import { useDocIssues } from "../review/api";
import type { BlockType } from "@kss/schema";
import { ClipboardCopy } from "lucide-react";
import { copyBlocks } from "../blocks/clipboard/store";
import { ExportPageButton } from "../transfer/ExportPageButton";
import { PageActions } from "./PageActions";

const WIDTHS = { narrow: "max-w-3xl", wide: "max-w-5xl", full: "max-w-none" } as const;
type PageWidth = keyof typeof WIDTHS;
const WIDTH_KEY = "page-width";

export function PageEditorPage() {
  const { workspaceId, docId } = useParams({ strict: false }) as { workspaceId: string; docId: string };
  const t = useT();
  const me = useMe();
  const page = usePage(docId);
  const ws = useWorkspace(workspaceId);
  const handle = useDoc("page", docId);
  const status = useSyncStatus(handle);
  const issues = useDocIssues(workspaceId, docId);
  const [width, setWidth] = useState<PageWidth>("narrow");
  // a per-reader view preference, not document content: kept out of Loro (I1)
  useEffect(() => {
    void getMeta<PageWidth>(WIDTH_KEY).then((w) => w && w in WIDTHS && setWidth(w));
  }, []);
  const changeWidth = (next: PageWidth) => {
    setWidth(next);
    void setMeta(WIDTH_KEY, next);
  };
  const readOnly = handle?.mode === "read" || page.data?.my_mode === "read";
  const [views] = useState(() => new Map<TreeID, FocusTarget>());
  const [focusedBy, setFocusedBy] = useState(new Map<string, Array<{ name: string; color: string }>>());
  const [copiedAll, setCopiedAll] = useState(false);
  const [linkTarget, setLinkTarget] = useState<{ treeId: TreeID; blockType: BlockType } | null>(null);
  // [M24] the page itself as a link subject (spec §8.25, Q73): its workspace links and facts
  const [pageLinks, setPageLinks] = useState(false);
  const [roleTarget, setRoleTarget] = useState<TreeID | null>(null);
  const wsHandle = useDoc("workspace", workspaceId);
  const sequences = useLoroSelector(wsHandle?.doc ?? null, readSequences, (a, b) => a.length === b.length && a.every((x, i) => x.id === b[i]!.id && x.label === b[i]!.label)) ?? [];

  const ctx = useMemo<PageEditorContextValue | null>(() => {
    if (!handle) return null;
    // One page-level UndoManager (spec §8.7); origins prefixed `sys:` are excluded.
    const undoManager = new UndoManager(handle.doc, { excludeOriginPrefixes: ["sys:"], mergeInterval: 500 });
    const cursors = new CursorEphemeralStore(handle.doc.peerIdStr);
    return {
      undoManager,
      cursors,
      registerView: (id, v) => views.set(id, v),
      unregisterView: (id) => views.delete(id),
      focusBlock: (id, edge) => {
        const v = views.get(id);
        if (!v) {
          document.querySelector<HTMLElement>(`[data-block-id="${id}"]`)?.scrollIntoView({ block: "center" });
          return;
        }
        v.focusEdge(edge);
      },
    };
  }, [handle, views]);

  // only initialise once the join attempt has finished: writing into a doc whose server state has
  // not arrived yet would overwrite its title with the empty one created here
  const joinSettled = useJoinSettled(handle);
  useEffect(() => {
    if (!handle || !me.data || readOnly || !joinSettled) return;
    ensurePageInit(handle.doc, me.data.id, page.data?.title ?? "");
  }, [handle, me.data, readOnly, joinSettled, page.data?.title]);

  // Global undo keybindings route to the page-level manager (spec §8.7).
  useEffect(() => {
    if (!ctx) return;
    const onKey = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;
      if (!mod) return;
      // an editor that already undid through the same manager (ProseMirror's keymap) marks the event:
      // undoing again here would take back a second step
      if (e.defaultPrevented) return;
      if (e.key === "z" && !e.shiftKey) { e.preventDefault(); ctx.undoManager.undo(); }
      else if ((e.key === "z" && e.shiftKey) || e.key === "y") { e.preventDefault(); ctx.undoManager.redo(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [ctx]);

  // Presence: publish our focus, collect others' (spec §8.12).
  useEffect(() => {
    if (!handle || !me.data) return;
    const sub = handle.presence.subscribe(() => {
      const map = new Map<string, Array<{ name: string; color: string }>>();
      for (const f of allFocus(handle.presence, handle.doc.peerIdStr)) {
        if (!f.treeId) continue;
        map.set(f.treeId, [...(map.get(f.treeId) ?? []), { name: f.name, color: f.color }]);
      }
      setFocusedBy(map);
    });
    return () => sub();
  }, [handle, me.data]);

  // text cursors travel through the presence room (spec §8.12)
  const cursorName = me.data ? me.data.display_name || me.data.username : null;
  const cursorColor = me.data?.color ?? null;
  useEffect(() => {
    if (!handle || !ctx || !cursorName || !cursorColor) return;
    return bridgeCursors(handle.presence, ctx.cursors, handle.doc.peerIdStr, { name: cursorName, color: cursorColor });
  }, [handle, ctx, cursorName, cursorColor]);

  // Scroll to #block=<TreeID>
  useEffect(() => {
    if (!ctx) return;
    const m = location.hash.match(/block=([^&]+)/);
    if (m) setTimeout(() => ctx.focusBlock(decodeURIComponent(m[1]!) as TreeID), 300);
  }, [ctx]);

  if (!handle || !ctx || !me.data) return <div className="p-6 text-sm text-neutral-500">{t("page.opening")}</div>;
  const user = me.data;
  return (
    <PageEditorContext.Provider value={ctx}>
      <div className="flex h-full flex-col">
        <div className="sticky top-0 z-10 flex items-center gap-3 border-b border-neutral-200 bg-[var(--ground)] px-7 py-3 dark:border-neutral-800">
          <Link to="/w/$workspaceId" params={{ workspaceId }} className="text-[13.5px] text-neutral-500 transition hover:text-neutral-900 dark:hover:text-neutral-100">
            {t("page.backToWorkspace")}
          </Link>
          <PageTitle handle={handle} readOnly={!!readOnly} />
          <div className="grow" />
          {readOnly && <span className="kss-pill border-neutral-200 bg-neutral-100 text-neutral-500 dark:border-neutral-700 dark:bg-neutral-800">{t("ws.readOnly")}</span>}
          <select
            className="kss-control kss-field bg-transparent px-2 py-1 text-xs"
            value={width}
            onChange={(e) => changeWidth(e.target.value as PageWidth)}
            aria-label={t("page.width")}
            title={t("page.width")}
            data-testid="page-width"
          >
            <option value="narrow">{t("page.width.narrow")}</option>
            <option value="wide">{t("page.width.wide")}</option>
            <option value="full">{t("page.width.full")}</option>
          </select>
          <button
            className="flex items-center gap-1 rounded px-2 py-0.5 text-xs hover:bg-neutral-200 dark:hover:bg-neutral-800"
            title={t("page.copyAll.title")}
            data-testid="copy-all-blocks"
            data-copied={copiedAll ? "true" : "false"}
            onClick={() =>
              void copyBlocks({ doc: handle.doc, docId, workspaceId, pageTitle: pageTitleText(handle.doc)?.toString() ?? page.data?.title ?? "", page: true }).then(() => {
                setCopiedAll(true);
                setTimeout(() => setCopiedAll(false), 2500);
              })
            }
          >
            <ClipboardCopy size={14} /> {copiedAll ? t("page.copied") : t("page.copyAll")}
          </button>
          <ExportPageButton docId={docId} status={status} />
          <RelationsMenu handle={handle} wsDoc={wsHandle?.doc ?? null} workspaceId={workspaceId} readOnly={!!readOnly} />
          {!readOnly && <ExtractButton workspaceId={workspaceId} docId={docId} />}
          <PinToggle handle={handle} />
          <PageActions workspaceId={workspaceId} docId={docId} title={pageTitleText(handle.doc)?.toString() ?? page.data?.title ?? ""} readOnly={!!readOnly} onLinkPage={wsHandle ? () => { setLinkTarget(null); setPageLinks(true); } : undefined} />
          <SyncBadge status={status} />
        </div>
        <div className="flex min-h-0 grow">
          <div className={`mx-auto min-h-0 w-full grow ${WIDTHS[width]}`} data-width={width}>
            <BlockTree
              handle={handle}
              readOnly={!!readOnly}
              userId={user.id}
              focusedBy={focusedBy}
              onFocusBlock={(id) => setFocus(handle.presence, handle.doc.peerIdStr, { userId: user.id, name: user.display_name || user.username, color: user.color, treeId: id })}
              onLinkBlock={(treeId, blockType) => setLinkTarget({ treeId, blockType })}
              onSetRole={(treeId) => setRoleTarget(treeId)}
              sequences={sequences}
              workspaceId={workspaceId}
              issues={issues.data}
              onAddToSequence={(seqId, treeId) => wsHandle && addMember(wsHandle.doc, seqId, blockIri(docId, treeId))}
              wsDoc={wsHandle?.doc ?? null}
            />
          </div>
          {roleTarget && <RoleDialog handle={handle} treeId={roleTarget} userId={user.id} readOnly={!!readOnly} onClose={() => setRoleTarget(null)} />}
          {pageLinks && wsHandle && !linkTarget && (
            <LinksPanel
              subject={pageSubject(wsHandle, workspaceId, docId, pageNodeOf(wsHandle.doc, docId))}
              wsHandle={wsHandle}
              workspaceId={workspaceId}
              userId={user.id}
              readOnly={ws.data?.my_role === "viewer"}
              title={pageTitleText(handle.doc)?.toString() || t("page.untitled")}
              onClose={() => setPageLinks(false)}
            />
          )}
          {linkTarget && (
            <LinksPanel
              subject={blockSubject(handle, linkTarget.treeId, linkTarget.blockType)}
              wsHandle={wsHandle}
              workspaceId={workspaceId}
              userId={user.id}
              readOnly={!!readOnly}
              onClose={() => setLinkTarget(null)}
            />
          )}
        </div>
      </div>
    </PageEditorContext.Provider>
  );
}

function PageTitle({ handle, readOnly }: { handle: NonNullable<ReturnType<typeof useDoc>>; readOnly: boolean }) {
  const tr = useT();
  const title = useLoroSelector(handle.doc, (d) => pageTitleText(d)?.toString() ?? "") ?? "";
  const [draft, setDraft] = useState<string | null>(null);
  return (
    <input
      className="min-w-0 grow bg-transparent text-lg font-semibold outline-none"
      placeholder={tr("page.titlePlaceholder")}
      value={draft ?? title}
      readOnly={readOnly}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        if (draft !== null && draft !== title) {
          let t = pageTitleText(handle.doc);
          if (!t) t = handle.doc.getMap("meta").setContainer("title", new LoroText());
          t.update(draft);
          handle.doc.commit();
        }
        setDraft(null);
      }}
      data-testid="page-title"
    />
  );
}
